import { useCallback, useEffect, useRef } from 'react';
import { employeeAPI } from '../utils/api';
import { usePickerSearch } from './usePickerSearch';
import type { EmployeeOption } from '../types/employee';

export function useEmployeeSearch(companyId?: string, includeInactive = false, enabled = true, initialSearch = '') {
  const load = useCallback(async (search: string, signal: AbortSignal): Promise<EmployeeOption[]> => {
    if (!companyId) return [];
    const response = await employeeAPI.getEmployees(companyId, {
      fields: 'minimal', search, limit: 20, ...(includeInactive ? { all_employees: 1 } : {}),
    }, signal);
    const options = response.employees || [];
    if (!includeInactive || !options.some(employee => employee.is_active_today === undefined)) return options;
    // The minimal payload has no employment dates. Compare the same bounded
    // active search instead of downloading full employee objects for labels.
    const active = await employeeAPI.getEmployees(companyId, { fields: 'minimal', search, limit: 20 }, signal);
    if (!active.success) throw new Error('Could not load active employee options');
    const activeIds = new Set(active.employees.map(employee => employee.id));
    return options.map(employee => ({ ...employee, is_active_today: employee.is_active_today ?? activeIds.has(employee.id) }));
  }, [companyId, includeInactive]);
  const picker = usePickerSearch(`${companyId}:${includeInactive}`, load, enabled && !!companyId, initialSearch);
  const { setSearch } = picker;
  const previousCompany = useRef(companyId);
  useEffect(() => {
    if (previousCompany.current !== companyId) setSearch(initialSearch);
    previousCompany.current = companyId;
  }, [companyId, initialSearch, setSearch]);
  return picker;
}
